import React, { useEffect, useState } from 'react';
import api from '../../services/api';

/* ── Colour / icon map per event type ──────────────────────── */
const TYPE_META = {
  joining:        { dot: '#10b981', bg: '#d1fae5', label: 'Joined',          icon: '🚀' },
  joining_salary: { dot: '#10b981', bg: '#d1fae5', label: 'Starting Salary', icon: '💼' },
  confirmation:   { dot: '#3b82f6', bg: '#dbeafe', label: 'Confirmed',        icon: '✅' },
  hike:           { dot: '#8b5cf6', bg: '#ede9fe', label: 'Salary Hike',      icon: '📈' },
  promotion:      { dot: '#f59e0b', bg: '#fef3c7', label: 'Promotion',        icon: '⭐' },
  adjustment:     { dot: '#64748b', bg: '#f1f5f9', label: 'Adjustment',       icon: '🔧' },
  exit:           { dot: '#ef4444', bg: '#fee2e2', label: 'Exited',           icon: '🚪' },
  current:        { dot: '#0ea5e9', bg: '#e0f2fe', label: 'Today',            icon: '📍' },
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const fmtSalary = (n, cur = 'INR') => {
  if (!n) return null;
  const v = Number(n);
  const sym = cur === 'INR' ? '₹' : '$';
  if (v >= 100000) return `${sym}${(v / 100000).toFixed(2)}L`;
  if (v >= 1000)   return `${sym}${(v / 1000).toFixed(0)}K`;
  return `${sym}${v}`;
};

/* ── Component ─────────────────────────────────────────────── */
/**
 * Props:
 *   endpoint — string  e.g. "/hr/me/timeline" or "/hr/42/timeline"
 *   name     — string  employee display name (for header)
 */
export default function CareerTimeline({ endpoint, name }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (!endpoint) return;
    setLoading(true);
    setError(null);
    api.get(endpoint)
      .then(r => setEvents(r.data.data || []))
      .catch(() => setError('Failed to load timeline.'))
      .finally(() => setLoading(false));
  }, [endpoint]);

  if (loading) return (
    <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
      Loading timeline…
    </div>
  );

  if (error) return (
    <div style={{ padding: '16px', background: '#fee2e2', borderRadius: 8, color: '#dc2626', fontSize: '0.83rem' }}>
      {error}
    </div>
  );

  if (events.length === 0) return (
    <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
      No timeline events yet. Add a joining date or pay revision to get started.
    </div>
  );

  /* total duration label */
  const first = events.find(e => e.type === 'joining');
  const durationLabel = (() => {
    if (!first) return null;
    const ms  = Date.now() - new Date(first.date).getTime();
    const yrs = Math.floor(ms / (1000 * 60 * 60 * 24 * 365));
    const mos = Math.floor((ms % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
    if (yrs > 0) return `${yrs}y ${mos}m with the company`;
    return `${mos} month${mos !== 1 ? 's' : ''} with the company`;
  })();

  return (
    <div>
      {/* Header row */}
      {(name || durationLabel) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          {name && (
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {name}
            </div>
          )}
          {durationLabel && (
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', background: '#f8fafc', padding: '4px 12px', borderRadius: 20, border: '1px solid #e2e8f0' }}>
              {durationLabel}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: 36 }}>
        {/* vertical line */}
        <div style={{
          position: 'absolute', left: 11, top: 10, bottom: 10,
          width: 2, background: 'linear-gradient(to bottom, #d1fae5, #e0f2fe)',
        }} />

        {events.map((ev, idx) => {
          const meta = TYPE_META[ev.type] || TYPE_META.adjustment;
          const isLast = idx === events.length - 1;

          return (
            <div key={idx} style={{ position: 'relative', marginBottom: isLast ? 0 : 24 }}>
              {/* dot */}
              <div style={{
                position: 'absolute', left: -27, top: 2,
                width: 20, height: 20, borderRadius: '50%',
                background: meta.dot,
                border: '3px solid #fff',
                boxShadow: `0 0 0 2px ${meta.dot}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem',
                zIndex: 1,
              }}>
                <span style={{ lineHeight: 1 }}>{meta.icon}</span>
              </div>

              {/* card */}
              <div style={{
                background: '#fff',
                border: `1px solid ${meta.dot}30`,
                borderLeft: `3px solid ${meta.dot}`,
                borderRadius: 10,
                padding: '12px 16px',
              }}>
                {/* top row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase',
                      letterSpacing: '0.07em', background: meta.bg, color: meta.dot,
                      padding: '2px 8px', borderRadius: 20,
                    }}>
                      {meta.label}
                    </span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>{ev.title}</span>
                  </div>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500, flexShrink: 0 }}>
                    {fmtDate(ev.date)}
                  </span>
                </div>

                {/* subtitle / designation */}
                {ev.subtitle && (
                  <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 4 }}>{ev.subtitle}</div>
                )}

                {/* salary info */}
                {(ev.new_salary || ev.salary) && (() => {
                  const cur = ev.currency || 'INR';
                  const newAmt = ev.new_salary || ev.salary;
                  return (
                    <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                      {ev.old_salary && (
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                          From <b style={{ textDecoration: 'line-through' }}>{fmtSalary(ev.old_salary, cur)}</b>
                        </div>
                      )}
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                        {ev.old_salary ? '→ ' : ''}{fmtSalary(newAmt, cur)}/yr
                      </div>
                      {ev.pct && (
                        <div style={{
                          fontSize: '0.75rem', fontWeight: 700,
                          color: Number(ev.pct) >= 0 ? '#059669' : '#dc2626',
                          background: Number(ev.pct) >= 0 ? '#d1fae5' : '#fee2e2',
                          borderRadius: 20, padding: '1px 8px',
                        }}>
                          {Number(ev.pct) >= 0 ? '+' : ''}{Number(ev.pct).toFixed(1)}%
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* notes */}
                {ev.notes && (
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>
                    {ev.notes}
                  </div>
                )}

                {/* recorded by */}
                {ev.recorded_by && (
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 }}>
                    Recorded by {ev.recorded_by}
                  </div>
                )}

                {/* current status badge */}
                {ev.type === 'current' && ev.status && (
                  <div style={{ marginTop: 6 }}>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                      background: ev.status === 'active' ? '#d1fae5' : '#fef3c7',
                      color: ev.status === 'active' ? '#065f46' : '#92400e',
                      padding: '2px 10px', borderRadius: 20,
                    }}>
                      {ev.status.replace('_', ' ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
